import { useState, useEffect, useMemo } from "react";
import { Header } from "@/components/Header";
import { StaticRouteBreadcrumbs } from "@/components/StaticRouteBreadcrumbs";
import { Footer } from "@/components/Footer";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import { FAQJsonLd } from "@/components/JsonLd";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { FAQ_SECTIONS } from "../../scripts/site-policy.mjs";

import { routeHead } from "@/lib/routeHead";

// Title/description come from scripts/routes.mjs, like the prerendered head.
const head = routeHead("/faq");

// Shared with the prerendered /faq body (scripts/site-policy.mjs).
const faqSections = FAQ_SECTIONS as { category: string; faqs: { q: string; a: string }[] }[];

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const FAQ = () => {
  const [query, setQuery] = useState("");
  const [openValue, setOpenValue] = useState<string | undefined>(undefined);

  // Open item from URL hash
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      for (const section of faqSections) {
        const idx = section.faqs.findIndex((f) => slug(f.q) === hash);
        if (idx >= 0) {
          setOpenValue(`${section.category}-${idx}`);
          setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
          break;
        }
      }
    }
  }, []);

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return faqSections;
    return faqSections
      .map((s) => ({ ...s, faqs: s.faqs.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)) }))
      .filter((s) => s.faqs.length > 0);
  }, [query]);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{head.title}</title>
        <meta name="description" content={head.description} />
        <link rel="canonical" href="https://loftloom.com/faq" />
        <meta property="og:title" content={head.title} />
        <meta property="og:description" content={head.description} />
        <meta property="og:url" content="https://loftloom.com/faq" />
        <meta property="og:type" content="website" />
      </Helmet>
      <FAQJsonLd questions={faqSections.flatMap(s => s.faqs.map(f => ({ question: f.q, answer: f.a })))} />
      <AnnouncementBar />
      <Header />
      <StaticRouteBreadcrumbs />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-16 md:py-24">
          <h1 className="page-title font-semibold mb-4 text-center">Frequently Asked Questions</h1>
          <p className="text-muted-foreground text-center max-w-xl mx-auto mb-8">Find answers to common questions about orders, shipping, and more.</p>

          {/* Search */}
          <div className="relative max-w-md mx-auto mb-10">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions…"
              className="h-12 pl-10 pr-4"
              aria-label="Search FAQ"
            />
          </div>

          <div className="space-y-10">
            {filteredSections.length === 0 && (
              <p className="text-center text-sm text-muted-foreground">No results for "{query}". Try different keywords.</p>
            )}
            {filteredSections.map((section) => (
              <div key={section.category} id={section.category.toLowerCase().split(" ")[0]} className="scroll-mt-24">
                <h2 className="text-sm font-semibold uppercase tracking-widest text-primary mb-4">{section.category}</h2>
                <Accordion type="single" collapsible className="w-full" value={openValue} onValueChange={setOpenValue}>
                  {section.faqs.map((faq, i) => {
                    const id = slug(faq.q);
                    return (
                      <AccordionItem key={i} value={`${section.category}-${i}`} id={id} className="scroll-mt-24">
                        <AccordionTrigger className="text-left text-[15px] font-medium">
                          <a
                            href={`#${id}`}
                            onClick={(e) => { e.stopPropagation(); window.history.replaceState(null, "", `#${id}`); }}
                            className="hover:text-primary transition-colors"
                          >
                            {faq.q}
                          </a>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FAQ;
